<template>
<div>
  <!-- Section Participants -->
  <div class="card mb-4">
    <div class="card-header d-flex justify-content-between align-items-center">
      <h5 class="mb-0">
        <i class="fa-solid fa-users me-2"></i>
        Participant{{ totalParticipants > 1 ? 's' : '(e)' }}
      </h5>
      <span v-if="totalParticipants > 1" class="badge bg-primary">{{ totalParticipants }} personne<span v-if="totalParticipants > 1">s</span></span>
    </div>
    <div class="card-body">
      <!-- Participant principal -->
      <div class="mb-3">
        <div class="fw-bold mb-2" v-if="totalParticipants > 1">
          <i class="fa-solid fa-user me-2"></i>
          Participant(e){{totalParticipants > 1 ? ' principal(e)' : ''}}
        </div>
        <div class="ps-4">
          <div>
            {{ GetCivilityShortLabel(participation?.userInfo?.civility) }}
            {{ participation?.userInfo?.firstName }}
            {{ participation?.userInfo?.lastName?.toUpperCase() }}
            <span v-if="participation?.userInfo?.status && participation?.userInfo?.status !== 'Autre'" class="badge border border-primary text-primary ms-2">
              {{ GetUserStatusLabel(participation.userInfo.status) }}
            </span>
          </div>
          <div class="text-muted small mt-1">
            <span v-if="participation?.userInfo?.email">
              <i class="fa-solid fa-envelope me-1"></i>{{ participation.userInfo.email }}
            </span>
            <span v-if="participation?.userInfo?.mobilePhone" class="ms-3 text-nowrap">
              <i class="fa-solid fa-phone me-1"></i>{{ participation.userInfo.mobilePhone }}
            </span>
          </div>
        </div>
      </div>

      <!-- Accompagnants -->
      <template v-if="participation?.companions && participation.companions.length > 0">
        <hr />
        <div class="fw-bold mb-2">
          <i class="fa-solid me-2" :class="[ participation.companions.length > 1 ? 'fa-user-group' : 'fa-user' ]"></i>
          {{ GetCompanionLabel(participation.travel.companionLabel, participation.companions.length, true) }}
        </div>
        <div v-for="(companion, index) in participation.companions" :key="index" class="ps-4 mb-2">
          <div>
            {{ GetCivilityShortLabel(companion.civility) }}
            {{ companion.firstName }}
            {{ companion.lastName?.toUpperCase() }}
            <span v-if="companion.relation" class="badge border border-primary text-primary ms-2">
              {{ GetRelationLabel(companion.relation) }}
            </span>
            <span v-if="companion.status && companion.status !== 'Autre'" class="badge border border-primary text-primary ms-1">
              {{ GetUserStatusLabel(companion.status) }}
            </span>
          </div>
          <div class="text-muted small mt-1">
            <span v-if="companion.email">
              <i class="fa-solid fa-envelope me-1"></i>{{ companion.email }}
            </span>
            <span v-if="companion.mobilePhone" class="ms-3">
              <i class="fa-solid fa-phone me-1"></i>{{ companion.mobilePhone }}
            </span>
          </div>
        </div>
      </template>
    </div>
  </div>

  <!-- Section Package -->
  <div class="card mb-4" v-if="packageCost > 0">
    <div class="card-header d-flex justify-content-between align-items-center border-bottom-0">
      <h5 class="mb-0">
        <i class="fa-solid fa-box me-2"></i>
        Package
      </h5>
      <div>
        <span v-if="packageCost > 0" class="badge bg-primary">
          {{ packageCostLabel }} {{ formatNumber(packageCost) }}€
        </span>
      </div>
    </div>
  </div>

  <!-- Section Transport -->
  <div class="card mb-4" v-if="(participation?.travel.transportations?.length??0) > 0">
    <div class="card-header d-flex justify-content-between align-items-center">
      <h5 class="mb-0">
        <i class="fa-solid fa-plane me-2"></i>
        Transport
      </h5>
      <span class="badge" :class="transportCost == 0 ? 'bg-success' : 'bg-primary'">
        {{ formatPrice(transportCost) }}
      </span>
    </div>
    <div class="card-body">
      <!-- Cas particulier (extension) ou  Dates personnalisées (sans package) ou Sans Vol -->
      <template v-if="participation?.travelInfo?.specialCase || participation?.travelInfo?.withPackage === false || participation?.travelInfo?.withoutTransport">
        <div class="d-flex align-items-center mb-3">
          <i class="fa-solid fa-exclamation-triangle text-warning me-3 fa-lg"></i>
          <div v-if="participation?.travelInfo?.specialCase">
            <div class="fw-bold">Cas Particulier</div>
            <div class="text-muted small">Extension séjour avant ou après</div>
          </div>
          <div v-else-if="participation?.travelInfo?.withoutTransport">
            <div class="fw-bold">Sans Vol</div>
            <div class="text-muted small">Vols et transferts à votre charge</div>
          </div>
          <div v-else>
            <div class="fw-bold">Dates personnalisées</div>
          </div>
        </div>
        <template v-if="hasCustomFlightDetails">
          <hr />
          <div class="row">
            <div class="col-12 col-md-6 mb-3 mb-md-0">
              <div class="fw-bold"><i class="fa-solid fa-plane-departure me-2"></i>Vol Aller</div>
              <div class="ps-4 small">
                <div v-if="participation?.transportation?.outboundCompany">Compagnie: {{ participation.transportation.outboundCompany }}</div>
                <div v-if="participation?.transportation?.outboundFlightNumber">Vol: {{ participation.transportation.outboundFlightNumber }}</div>
                <div v-if="participation?.transportation?.outboundAirport">Aéroport: {{ participation.transportation.outboundAirport }}</div>
                <div v-if="participation?.transportation?.outboundDate">Date: {{ formatDate(participation.transportation.outboundDate, 'DD/MM/YYYY') }}</div>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="fw-bold"><i class="fa-solid fa-plane-arrival me-2"></i>Vol Retour</div>
              <div class="ps-4 small">
                <div v-if="participation?.transportation?.returnCompany">Compagnie: {{ participation.transportation.returnCompany }}</div>
                <div v-if="participation?.transportation?.returnFlightNumber">Vol: {{ participation.transportation.returnFlightNumber }}</div>
                <div v-if="participation?.transportation?.returnAirport">Aéroport: {{ participation.transportation.returnAirport }}</div>
                <div v-if="participation?.transportation?.returnDate">Date: {{ formatDate(participation.transportation.returnDate, 'DD/MM/YYYY') }}</div>
              </div>
            </div>
          </div>
        </template>
      </template>

      <!-- Package standard avec transport sélectionné -->
      <template v-else-if="selectedTransport">
        <div class="d-flex flex-column flex-md-row">
          <div class="me-4 mb-2 mb-md-0">
            <i :class="getTransportIcon(selectedTransport.type)" class="text-primary fa-lg"></i>
          </div>
          <div class="flex-grow-1">
            <div class="row">
              <div class="col-12 col-md-5 mb-2 mb-md-0">
                <div class="fw-bold" v-html="'Aller - ' + formatNameToHtml(selectedTransport.outboundItinerary)"></div>
                <div class="text-muted small">{{ selectedTransport.outboundDate }}</div>
                <div class="text-muted small">{{ selectedTransport.outboundCompany }}</div>
              </div>
              <div class="col-12 col-md-2 text-center d-none d-md-block">
                <i class="fa-solid fa-arrow-right text-muted"></i>
              </div>
              <div class="col-12 col-md-5">
                <div class="fw-bold" v-html="'Retour - ' + formatNameToHtml(selectedTransport.returnItinerary)"></div>
                <div class="text-muted small">{{ selectedTransport.returnDate }}</div>
                <div class="text-muted small">{{ selectedTransport.returnCompany }}</div>
              </div>
            </div>
            <div v-if="selectedTransportOption" class="mt-2">
              <span class="small text-primary">
                {{ selectedTransportOption.name }}
                <span v-if="selectedTransportOption.price > 0">(+{{ selectedTransportOption.price }}€ / siège.)</span>
              </span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>

  <!-- Section Transferts -->
  <template v-if="hasTransfers">
    <div class="card mb-4">
      <div
        class="card-header d-flex justify-content-between align-items-center"
        :class="{'border-bottom-0' : !(outboundTransfer || returnTransfer || roundTripTransfer) }">
        <h5 class="mb-0">
          <i class="fa-solid fa-bus me-2"></i>
          Transferts
        </h5>
        <span class="badge" :class="transferCost < 0 ? 'bg-success' : (transferCost > 0 ? 'bg-primary' : 'bg-success')">
        {{ formatPrice(transferCost) }}
      </span>
      </div>
      <div v-if="outboundTransfer || returnTransfer || roundTripTransfer" class="card-body">
        <div class="row">
          <!-- Transfert Aller-Retour -->
          <div v-if="roundTripTransfer" class="col-12 col-md-6 mb-3 mb-md-0">
            <div class="fw-bold mb-2">
              <i :class="getTransferIcon(roundTripTransfer.type)" class="me-2"></i>
              Transfert Aller-Retour
            </div>
            <div class="ps-4">
              <div>{{ roundTripTransfer.name }}</div>
              <div class="text-muted small">
                <div v-if="roundTripTransfer.startDate">
                  <i class="fa-solid fa-calendar me-1"></i>
                  {{ formatDate(roundTripTransfer.startDate, 'DD/MM/YYYY HH:mm') }}
                </div>
                <div v-if="roundTripTransfer.meetingPoint">
                  <i class="fa-solid fa-location-dot me-1"></i>
                  {{ roundTripTransfer.meetingPoint }}
                </div>
              </div>
            </div>
          </div>


          <!-- Transfert Aller -->
          <div v-if="outboundTransfer" class="col-12 col-md-6 mb-3 mb-md-0">
            <div class="fw-bold mb-2">
              <i :class="getTransferIcon(outboundTransfer.type)" class="me-2"></i>
              Transfert Aller
            </div>
            <div class="ps-4">
              <div>{{ outboundTransfer.name }}</div>
              <div class="text-muted small">
                <div v-if="outboundTransfer.startDate">
                  <i class="fa-solid fa-calendar me-1"></i>
                  {{ formatDate(outboundTransfer.startDate, 'DD/MM/YYYY HH:mm') }}
                </div>
                <div v-if="outboundTransfer.meetingPoint">
                  <i class="fa-solid fa-location-dot me-1"></i>
                  {{ outboundTransfer.meetingPoint }}
                </div>
              </div>
            </div>
          </div>

          <!-- Transfert Retour -->
          <div v-if="returnTransfer" class="col-12 col-md-6">
            <div class="fw-bold mb-2">
              <i :class="getTransferIcon(returnTransfer.type)" class="me-2"></i>
              Transfert Retour
            </div>
            <div class="ps-4">
              <div>{{ returnTransfer.name }}</div>
              <div class="text-muted small">
                <div v-if="returnTransfer.startDate">
                  <i class="fa-solid fa-calendar me-1"></i>
                  {{ formatDate(returnTransfer.startDate, 'DD/MM/YYYY HH:mm') }}
                </div>
                <div v-if="returnTransfer.meetingPoint">
                  <i class="fa-solid fa-location-dot me-1"></i>
                  {{ returnTransfer.meetingPoint }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  <template v-else-if="!participation?.travelInfo.withPackage && (participation?.travel?.transfers?.length ?? 0) > 0">
    <div class="card mb-4">
      <div class="card-header d-flex justify-content-between align-items-center border-bottom-0">
        <h5 class="mb-0">
          <i class="fa-solid fa-bus me-2"></i>
          Transferts
        </h5>
        <span class="badge bg-secondary">
          Sans transfert
        </span>
      </div>
    </div>
  </template>

  <!-- Section Hébergement -->
  <template v-if="participation?.hotelRooms && participation.hotelRooms.length > 0">
    <div class="card mb-4">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <i class="fa-solid fa-hotel me-2"></i>
          Hébergement
        </h5>
        <span class="badge" :class="hotelCost > 0 ? 'bg-primary' : 'bg-success'">
          {{ formatPrice(hotelCost) }}
        </span>
      </div>
      <div class="card-body">
        <div v-for="(room, index) in participation.hotelRooms" :key="room.id">
          <div class="d-flex flex-column flex-md-row justify-content-between">
            <div>
              <div class="fw-bold">
                <img v-if="room.type == TravelDbHotelRoomTypeEnum.Single" :src="singleBedIcon" alt="Lit Simple" />
                <span v-else-if="room.type == TravelDbHotelRoomTypeEnum.Twin">
                  <img :src="singleBedIcon" alt="Lit Simple" />
                  <img :src="singleBedIcon" alt="Lit Simple" />
                </span>
                <img v-else-if="room.type == TravelDbHotelRoomTypeEnum.Double" :src="doubleBedIcon" alt="Lit Double" />
                Chambre {{ index + 1 }} ( {{ GetHotelRoomTypeLabelShort(room.type) }} )
              </div>
              <div class="ps-4">
                <div v-if="findHotelByRoomId(room.roomId)">
                  <span class="text-muted">Hôtel :</span>
                  <span class="ms-1">{{ findHotelByRoomId(room.roomId)?.name }}</span>
                </div>
                <div v-if="findRoomById(room.roomId)">
                  <span class="text-muted">Chambre :</span>
                  <span class="ms-1">{{ findRoomById(room.roomId)?.name }}</span>
                </div>
                <div v-if="room.roommates && room.roommates.length > 0" class="d-flex text-muted small">
                  <i class="fa-solid fa-user-group me-1"></i>
                  <ul style="list-style-type: none;padding-left: 5px;margin-bottom: 0;">
                    <li v-for="roommate in room.roommates" :key="roommate.userId ? `#${roommate.userId}` : `${roommate.companionId}#`">
                      - {{ roommate.firstName }} {{ roommate.lastName?.toUpperCase() }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="mt-2 mt-md-0 d-flex flex-column align-items-end gap-3">
              <span class="text-muted small">
                <i class="fa-solid fa-moon me-1"></i>
                {{ participation?.travel?.nights + extraNights }} nuit<span v-if="participation?.travel?.nights + extraNights > 1">s</span>
              </span>
              <span v-if="getRoomPrice(room) === 0" class="badge border border-success text-success">Inclus</span>
              <span v-else class="small">
                {{ formatPrice(getRoomPrice(room)) }} / pers.
              </span>
            </div>
          </div>
          <hr v-if="index < participation.hotelRooms.length - 1" />
        </div>
      </div>
    </div>
  </template>

  <!-- Section Activités -->
  <template v-if="hasSelectedOptions">
    <div class="card mb-4">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <i class="fa-solid fa-person-snowboarding me-2"></i>
          Options
        </h5>
        <span v-if="activitiesCost > 0" class="badge bg-primary">
          {{ formatPrice(activitiesCost) }}
        </span>
      </div>
      <div class="card-body">
        <template v-for="option in participation?.options" :key="option.id">
          <template v-for="activity in option.activities?.filter(a => a.participantCount > 0)" :key="activity.id">
            <div class="d-flex flex-column flex-sm-row justify-content-between mb-2">
              <div>
                <span class="fw-bold">{{ activity.name }}</span>
                <span class="text-muted ms-2 text-nowrap">( {{ activity.participantCount }} participant<span v-if="activity.participantCount > 1">s</span> )</span>
              </div>
              <div>
                <span v-if="activity.noPrice" class="small text-secondary">N.C.</span>
                <span v-else-if="activity.price > 0" class="small text-nowrap">
                  {{ activity.participantCount }} x {{ formatPriceWithoutPlusSign(activity.price) }} = {{ formatPriceWithoutPlusSign(activity.participantCount * activity.price) }}
                </span>
                <span v-else class="small text-success">Inclus</span>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>
  </template>


  <!-- Section Documents -->
  <!-- TODO: Afficher les infos des documents également pour les accompagnants -->
  <template v-if="(participation?.documents?.length ?? 0) > 0">
    <div class="card mb-4">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <i class="fa-solid fa-file me-2"></i>
          Documents
        </h5>
      </div>
      <div class="card-body">
        <div v-for="doc in participation?.documents" :key="doc.id" class="mb-3">
          <div class="fw-bold">
            {{ doc.label }}
              <span v-if="doc.file != null" class="badge bg-success ms-3">Fourni(e)</span>
              <span v-else class="badge bg-secondary ms-3">Non fourni(e)</span>
          </div>
        </div>
      </div>
    </div>
  </template>

  <!-- Section Documents à signer -->
  <template v-if="participation?.signature?.signedDate">
    <div class="card mb-4">
      <div class="card-header d-flex flex-column flex-md-row justify-content-between align-items-center border-bottom-0">
        <h5 class="mb-0">
          <i class="fa-solid fa-file-signature me-2"></i>
          Document signé le {{ formatDate(participation?.signature?.signedDate, 'DD/MM/YYYY') }}
        </h5>
        <a v-if="participation?.signature?.signedDocument" :href="getFileUrl(participation.signature.signedDocument.key)" target="_blank" class="btn btn-link">
          <i class="fa-solid fa-pen-nib me-1 link-underline link-underline-opacity-0"></i>Télécharger
        </a>
      </div>
    </div>
  </template>

  <!-- Demandes particulières -->
  <template v-if="participation?.userInfo?.specialRequests || participation?.userInfo?.allergies">
    <div class="card mb-4">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <i class="fa-solid fa-bell-concierge me-2"></i>Demandes particulières</h5>
      </div>
      <div class="card-body">
        <div class="ms-2" v-if="participation?.userInfo?.specialRequests">{{ participation.userInfo.specialRequests }}</div>
        <div class="ms-2 fw-bold" v-if="participation?.userInfo?.allergies">Allergies: {{ participation.userInfo.allergies }}</div>
      </div>
    </div>
  </template>

  <!-- Section Discount -->
  <div class="card mb-4" v-if="discount > 0">
    <div class="card-header d-flex justify-content-between align-items-center border-bottom-0">
      <h5 class="mb-0">
        <i class="fa-solid fa-tag me-2"></i>
        {{ participation?.cost?.discountLabel ?? 'Réduction' }}
      </h5>
      <div>
        <span class="badge bg-success">
          {{ (participation?.cost?.discountType == TravelDbDiscountTypeEnum.Percent) ? `- ${participation?.cost?.originalDiscount ?? 0}%` : formatPriceWithoutPlusSign(-discount) }}
        </span>
      </div>
    </div>
  </div>

  <PageBottomMessage class="mb-3" message-key="pageSummary"></PageBottomMessage>

  <!-- Section Total -->
  <div class="card _bg-primary" v-if="grandTotal > 0">
    <div class="card-body p-0">

      <!-- Détail du calcul -->
      <div class="small p-3">
        <div class="d-flex justify-content-between">
          <span>Package</span>
          <span>{{ formatNumber(packageCost) }} €</span>
        </div>
        <div v-if="hasTransports" class="d-flex justify-content-between">
          <span>Transport</span>
          <span>{{ formatPrice(transportCost) }}</span>
        </div>
        <div v-if="hasTransfers" class="d-flex justify-content-between">
          <span>Transfert</span>
          <span>{{ formatPrice(transferCost) }}</span>
        </div>
        <div v-if="hasHotels" class="d-flex justify-content-between">
          <span>Hébergement</span>
          <span>{{ formatPrice(hotelCost) }}</span>
        </div>
        <div v-if="activitiesCost > 0" class="d-flex justify-content-between">
          <span>Options</span>
          <span>{{ formatPrice(activitiesCost) }}</span>
        </div>
        <div v-if="discount" class="d-flex justify-content-between fw-bold text-reset">
          <span>{{ participation?.cost?.discountLabel ?? 'Réduction' }} :</span>
          <span>{{ formatPrice(-discount) }}</span>
        </div>
      </div>

      <!-- <hr class="border-primary opacity-50" /> -->

      <div class="d-flex justify-content-between align-items-center bg-primary rounded text-white p-3">
        <div>
          <h4 class="mb-0">
            <i class="fa-solid fa-calculator me-2"></i>
            Total
          </h4>
        </div>
        <div class="text-end">
          <h2 class="mb-0">{{ formatNumber(grandTotal) }} € {{ participation?.travel?.priceTTC ? 'TTC' : 'HT' }}</h2>
        </div>
      </div>

    </div>
  </div>
</div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import {
  GetCivilityShortLabel,
  GetCompanionLabel,
  GetUserStatusLabel,
  GetRelationLabel,
  formatDate,
  getFileUrl,
  formatNameToHtml,
  GetHotelRoomTypeLabelShort,
  formatPrice,
  formatPriceWithoutPlusSign
} from '@/compositions/helper';
import singleBedIcon from '@/assets/icons/single-bed-icon-24.png'
import doubleBedIcon from '@/assets/icons/double-bed-icon-24.png'

const participation = defineModel<UserParticipation>();

// Nombre total de participants
const totalParticipants = computed(() => {
  return 1 + (participation.value?.companions?.length ?? 0);
});

// Trouver un transport par ID
const findTransportById = (id: number | null | undefined): UserTransportation | undefined => {
  if (!id) return undefined;
  return participation.value?.travel?.transportations?.find((t: UserTransportation) => t.id === id);
};

// Transport sélectionné
const selectedTransport = computed((): UserTransportation | undefined => {
  return findTransportById(participation.value?.transportation?.id);
});

// Option de transport sélectionnée
const selectedTransportOption = computed((): UserTransportationOption | undefined => {
  const optionId = participation.value?.transportation?.optionId;
  if (!optionId || !selectedTransport.value?.options) return undefined;
  return selectedTransport.value.options.find((o: UserTransportationOption) => o.id === optionId);
});

// Trouver un transfert par ID
const findTransferById = (id: number | null | undefined): UserTransfer | undefined => {
  if (!id) return undefined;
  return participation.value?.travel?.transfers?.find((t: UserTransfer) => t.id === id);
};

// Transferts sélectionnés
const outboundTransfer = computed((): UserTransfer | undefined => {
  return findTransferById(participation.value?.transfer?.outboundId);
});

const returnTransfer = computed((): UserTransfer | undefined => {
  return findTransferById(participation.value?.transfer?.returnId);
});

const roundTripTransfer = computed((): UserTransfer | undefined => {
  return findTransferById(participation.value?.transfer?.roundTripId);
});

const hasTransports = computed(() => {
  return (participation.value?.travel?.transportations?.length ?? 0) > 0;
});

const hasHotels = computed(() => {
  return (participation.value?.travel?.hotels?.length ?? 0) > 0;
});

const hasTransfers = computed(() => {
  return outboundTransfer.value
   || returnTransfer.value
   || roundTripTransfer.value
   || (
         (participation.value?.travelInfo?.withPackage == true)
      && ((participation.value?.travelInfo?.specialCase??false) == false)
      && ((participation.value?.travelInfo?.withoutTransport??false) == false)
      && ((participation.value?.travel?.transfers?.length ?? 0) > 0)
      );
});

// Trouver une chambre par ID
const findRoomById = (roomId: number): UserHotelRoom | undefined => {
  for (const hotel of (participation.value?.travel?.hotels ?? []) as UserHotel[]) {
    const room = hotel.rooms?.find(r => r.id === roomId);
    if (room) return room;
  }
  return undefined;
};

// Trouver l'hôtel contenant une chambre
const findHotelByRoomId = (roomId: number): UserHotel | undefined => {
  for (const hotel of (participation.value?.travel?.hotels ?? []) as UserHotel[]) {
    if (hotel.rooms?.some(r => r.id === roomId)) {
      return hotel;
    }
  }
  return undefined;
};

// Obtenir le prix d'une chambre selon le type par personne pour le séjour (prix de la chambre * (nombre de nuits + éventuelles nuits supplémentaires))
const getRoomPrice = (room: UserParticipationHotelRoom): number => {
  const roomDef = findRoomById(room.roomId);
  if (!roomDef) return 0;
  return roomDef.price * ((participation.value?.travel.nights ?? 0) + (participation.value?.travelInfo.extraNights ?? 0));
};



// Vérifier si des détails de vol personnalisé sont renseignés
const hasCustomFlightDetails = computed(() => {
  const t = participation.value?.transportation;
  return t?.outboundCompany || t?.outboundFlightNumber || t?.outboundAirport ||
         t?.returnCompany || t?.returnFlightNumber || t?.returnAirport;
});

// Vérifier si des activités sont sélectionnées
const hasSelectedOptions = computed(() => {
  return participation.value?.options?.some(option =>
    option.activities?.some(activity => activity.participantCount > 0)
  );
});

// Icône de transport
const getTransportIcon = (type: TravelDbTransportationTypeEnum): string => {
  switch (type) {
    case TravelDbTransportationTypeEnum.Vol: return 'fa-solid fa-plane';
    case TravelDbTransportationTypeEnum.Train: return 'fa-solid fa-train';
    case TravelDbTransportationTypeEnum.Autocar: return 'fa-solid fa-bus';
    default: return 'fa-solid fa-car';
  }
};

// Icône de transfert
const getTransferIcon = (type: TravelDbTransferTypeEnum): string => {
  switch (type) {
    case TravelDbTransferTypeEnum.Bus: return 'fa-solid fa-bus';
    case TravelDbTransferTypeEnum.Train: return 'fa-solid fa-train';
    case TravelDbTransferTypeEnum.Autocar: return 'fa-solid fa-bus';
    default: return 'fa-solid fa-car';
  }
};

const extraNights = computed(() => {
  return participation.value?.travelInfo?.extraNights ?? 0;
});

const formatNumber = (price: number): string => {
  return `${price.toLocaleString('fr-FR')}`;
};

// Calcul du coût transport
const transportCost = computed(() => {
  return participation.value?.cost?.transportation ?? 0;
});

const packagePrice = computed(() => {
  return participation.value?.travel?.packagePrice ?? 0;
});

const packageCost = computed(() => {
  return participation.value?.cost?.package ?? 0;
});

const childParticipantCount = computed(() => {
  return participation.value?.companions?.filter(companion => companion.relation === UserRelationEnum.Enfant).length ?? 0;
});

const standardParticipantCount = computed(() => {
  return totalParticipants.value - childParticipantCount.value;
});

const childPackagePrice = computed(() => {
  return participation.value?.travel?.childPackageCost ?? packagePrice.value;
});

const packageCostLabel = computed(() => {
  if (childParticipantCount.value > 0 && childPackagePrice.value !== packagePrice.value) {
    return `${standardParticipantCount.value} x ${formatNumber(packagePrice.value)} € + ${childParticipantCount.value} x ${formatNumber(childPackagePrice.value)} € =`;
  }

  return `${totalParticipants.value} x ${formatNumber(packagePrice.value)} € =`;
});

const transferCost = computed(() => {
  return participation.value?.cost?.transfer ?? 0;
});

// Calcul du coût hébergement
const hotelCost = computed(() => {
  return (participation.value?.cost?.hotelRooms ?? 0) + (participation.value?.cost?.hotelRoomsExtraDays ?? 0);
});

// Calcul du coût des activités
const activitiesCost = computed(() => {
  return participation.value?.cost?.options ?? 0;
});

const discount = computed(() => {
  return participation.value?.cost?.discount ?? 0;
});

// Total général
const grandTotal = computed(() => {
  return participation.value?.cost?.totalPrice ?? 0;
});
</script>
